import React from "react";
import "./MoveBox.css";
import { bemClassesFromModifiers, isOutOfViewport } from "../../constants/misc";
import ExpandIcon from "../svgs/expand";
import MoveBoxNavBar from "./MoveBoxNavBar";
import Fab from "../fab/fab";
import ExternalWindow from "../externalWindow/externalWindow";
import MoveBoxWrapper from "./MoveBoxWrapper";
import MoveBoxAppBar from "./MoveBoxAppBar";
import DropDownMenu from "../dropDownSelect/dropDownSelect";
import Button from "../button/button";
import DockingIcon, {
  DOCK_FLOAT,
  DOCK_LEFT,
  DOCK_RIGHT,
} from "../svgs/dockingIcons";
import v4 from "uuid";
import {
  getTryoutSettings,
  saveTryoutPosition,
  saveTryoutSize,
  saveTryoutDockDirection,
  saveTryoutMinified,
} from "../../utils/tryoutStorage";

const DEFAULT_WIDTH = 460;
const DEFAULT_HEIGHT = 500;

const isOverflown = (element) => {
  if (!element) return;
  const overflow = (el) => {
    return el.clientHeight < el.scrollHeight;
  };
  return (
    overflow(element) || (element.firstChild && overflow(element.firstChild))
  );
};

const DIRECTIONS = [DOCK_FLOAT, DOCK_LEFT, DOCK_RIGHT];
// Keep old constants for compatibility
const DIRECTION_BOTTOM = "bottom";

class MoveBox extends React.Component {
  state = {
    offset: [0, 0],
    isDown: false,
    minified: false,
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    externalWindow: false,
    dockDirection: null,
  };

  moveBox = React.createRef();
  moveBoxContent = React.createRef();

  moveToTop = () => {
    this.moveBox.current.style.zIndex = 1000;
  };

  onMinify = () => {
    const { onMinify, isTryoutWindow } = this.props;
    const newMinified = !this.state.minified;

    this.setState({
      minified: newMinified,
      externalWindow: false,
    });

    // Save minified state if this is a tryout window
    if (isTryoutWindow) {
      saveTryoutMinified(newMinified);
    }

    if (onMinify) {
      onMinify(newMinified);
    }
  };

  onClose = () => {
    const { onClose } = this.props;
    if (onClose) {
      onClose();
    }
  };

  onExternalWindowClose = () => {
    const { externalWindow } = this.state;
    if (externalWindow) {
      this.onClose();
    }
  };

  onMouseDown = (e) => {
    document.onmousemove = this.onMouseMove;
    document.onmouseup = this.onMouseUp;
    this.setState({
      isDown: true,
      offset: [
        this.moveBox.current.offsetLeft - e.clientX,
        this.moveBox.current.offsetTop - e.clientY,
      ],
    });
  };

  onMouseUp = () => {
    document.onmousemove = null;
    document.onmouseup = null;

    // Save position if this is a tryout window
    if (
      this.props.isTryoutWindow &&
      this.moveBox.current &&
      !this.state.dockDirection
    ) {
      const left = parseInt(this.moveBox.current.style.left, 10);
      const top = parseInt(this.moveBox.current.style.top, 10);
      if (!isNaN(left) && !isNaN(top)) {
        saveTryoutPosition(left, top);
      }
    }

    this.setState({
      isDown: false,
    });
  };

  changeEventPosition = (e) => {
    const { offset } = this.state;
    this.moveBox.current.style.left = e.clientX + offset[0] + "px";
    this.moveBox.current.style.top = e.clientY + offset[1] + "px";
    this.moveBox.current.style.bottom = "initial";
    this.moveBox.current.style.right = "initial";
    this.restrainBoxToWindow();
  };

  restrainBoxToWindow = () => {
    const element = this.moveBox.current;
    if (!element) return;
    const { out } = isOutOfViewport(element);
    if (this.moveBox.current.clientHeight > window.innerHeight) {
      this.moveBox.current.style.height =
        window.innerHeight - this.moveBox.current.offsetTop + "px";
    }
    if (out.left) {
      this.moveBox.current.style.left = "0px";
    }
    if (out.right) {
      this.moveBox.current.style.left =
        document.body.offsetWidth - this.moveBox.current.offsetWidth + "px";
    }
    if (out.bottom) {
      this.moveBox.current.style.top =
        window.innerHeight - this.moveBox.current.clientHeight + "px";
    }
    if (out.top) {
      this.moveBox.current.style.top = "0px";
    }
  };

  onMouseMove = (e) => {
    const { isDown } = this.state;
    e.preventDefault();
    if (isDown) {
      this.moveToTop();
      this.changeEventPosition(e);
    }
  };

  onTouchMove = (e) => {
    this.changeEventPosition(e.touches[0]);
  };

  onTouchEnd = (e) => {
    const touch = e.changedTouches[0];

    // Save position if this is a tryout window
    if (
      this.props.isTryoutWindow &&
      this.moveBox.current &&
      !this.state.dockDirection
    ) {
      const left = parseInt(this.moveBox.current.style.left, 10);
      const top = parseInt(this.moveBox.current.style.top, 10);
      if (!isNaN(left) && !isNaN(top)) {
        saveTryoutPosition(left, top);
      }
    }

    this.setState({
      offset: [touch.clientX, touch.clientY],
    });
  };

  onTouchStart = (e) => {
    this.moveToTop();
    const touch = e.changedTouches[0];
    this.setState({
      offset: [
        this.moveBox.current.offsetLeft - touch.clientX,
        this.moveBox.current.offsetTop - touch.clientY,
      ],
    });
  };

  componentDidMount() {
    const {
      minified = false,
      width = DEFAULT_WIDTH,
      height = DEFAULT_HEIGHT,
      dockDirection,
      isTryoutWindow,
    } = this.props;

    // Load saved settings if this is a tryout window
    let finalSettings = {
      width,
      height,
      minified,
      dockDirection,
    };

    if (isTryoutWindow) {
      const savedSettings = getTryoutSettings();
      finalSettings = {
        width: savedSettings.width || width,
        height: savedSettings.height || height,
        minified:
          savedSettings.minified !== undefined
            ? savedSettings.minified
            : minified,
        dockDirection:
          savedSettings.dockDirection !== undefined
            ? savedSettings.dockDirection
            : dockDirection,
      };

      // Apply saved position if available
      if (savedSettings.position && !savedSettings.dockDirection) {
        setTimeout(() => {
          if (this.moveBox.current) {
            this.moveBox.current.style.left =
              savedSettings.position.left + "px";
            this.moveBox.current.style.top = savedSettings.position.top + "px";
            this.moveBox.current.style.right = "initial";
            this.moveBox.current.style.bottom = "initial";
            this.restrainBoxToWindow();
          }
        }, 100);
      }
    }

    this.moveToTop();

    this.moveBoxContent.current.style.maxHeight = "100%";
    this.moveBox.current.style.width = finalSettings.width + "px";
    this.moveBox.current.style.height = finalSettings.height + "px";

    this.setState({
      dockDirection: finalSettings.dockDirection,
      width: finalSettings.width,
      height: finalSettings.height,
      minified: finalSettings.minified,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.state.externalWindow && prevState.externalWindow) {
      this.moveToTop();
    }
    if (
      !this.state.externalWindow &&
      this.state.dockDirection !== prevState.dockDirection
    ) {
      // If undocking (dockDirection is null), reset styles
      if (!this.state.dockDirection) {
        this.resetDockStyles();
      } else {
        this.dockBox(this.state.dockDirection);
      }
    }
    if (!this.state.minified && prevState.minified) {
      this.moveToTop();
    }
    // Only restrain window if not docked
    if (!this.state.dockDirection) {
      this.restrainBoxToWindow();
    }
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    if (
      nextProps.shouldToggle &&
      nextProps.shouldToggle !== this.props.shouldToggle
    ) {
      this.setState({
        minified: !this.state.minified,
      });
    }
    if (typeof nextProps.minified === "boolean") {
      this.setState({
        minified: nextProps.minified,
      });
    }
    if (nextProps.shouldOpen && this.state.minified) {
      this.setState({
        minified: false,
      });
      this.moveToTop();
    }
  }

  onResizeMouseUp = () => {
    document.onmousemove = null;
    document.onmouseup = null;

    // Save size if this is a tryout window
    if (this.props.isTryoutWindow && this.moveBox.current) {
      const width = parseInt(this.moveBox.current.style.width, 10);
      const height = parseInt(this.moveBox.current.style.height, 10);
      if (!isNaN(width) && !isNaN(height)) {
        saveTryoutSize(width, height);
      }
    }

    this.setState({
      isDown: false,
    });
  };

  onResizeMouseDown = (e) => {
    document.onmousemove = this.onResize;
    document.onmouseup = this.onResizeMouseUp;
    this.setState({
      isDown: true,
      offset: [
        this.moveBox.current.offsetWidth - e.clientX,
        this.moveBox.current.offsetHeight - e.clientY,
      ],
    });
  };

  onResize = (e) => {
    const { isDown, offset } = this.state;
    e.preventDefault();
    if (isDown) {
      const MIN_WIDTH = 300;
      const MIN_HEIGHT = 200;

      const newWidth = Math.max(MIN_WIDTH, e.clientX + offset[0]);
      const newHeight = Math.max(MIN_HEIGHT, e.clientY + offset[1]);

      this.moveBoxContent.current.style.maxHeight = "100%";
      this.moveBox.current.style.width = newWidth + "px";
      this.moveBox.current.style.height = newHeight + "px";
      this.restrainBoxToWindow();
    }
  };

  changeDockPosition = (direction) => {
    const { dockDirection } = this.state;
    const { isTryoutWindow } = this.props;

    // Toggle: if clicking same button, undock (set to null/float)
    // If clicking different button, dock to that position
    const newDockDirection =
      direction === DOCK_FLOAT
        ? null // Float means undocked
        : direction !== dockDirection
        ? direction
        : null;

    this.setState({
      dockDirection: newDockDirection,
      externalWindow: false, // Never use external window for now
    });

    // Save dock direction if this is a tryout window
    if (isTryoutWindow) {
      saveTryoutDockDirection(newDockDirection);
    }
  };

  resetDockStyles = () => {
    if (!this.moveBox.current) return;

    // Reset to default floating window state
    this.moveBox.current.style.position = "fixed";
    this.moveBox.current.style.width = this.state.width + "px";
    this.moveBox.current.style.height = this.state.height + "px";

    // Load saved position if this is a tryout window
    if (this.props.isTryoutWindow) {
      const savedSettings = getTryoutSettings();
      if (savedSettings.position) {
        this.moveBox.current.style.left = savedSettings.position.left + "px";
        this.moveBox.current.style.top = savedSettings.position.top + "px";
      } else {
        // Default position
        this.moveBox.current.style.left = "100px";
        this.moveBox.current.style.top = "100px";
      }
    } else {
      // Default position for non-tryout windows
      this.moveBox.current.style.left = "100px";
      this.moveBox.current.style.top = "100px";
    }

    this.moveBox.current.style.right = "initial";
    this.moveBox.current.style.bottom = "initial";
    this.restrainBoxToWindow();
  };

  dockBox = (direction) => {
    if (!direction || !this.moveBox.current) return;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const dockWidth = Math.min(400, windowWidth * 0.3); // 30% of window width or max 400px

    if (direction === DOCK_LEFT) {
      // Dock to left side
      this.moveBox.current.style.position = "fixed";
      this.moveBox.current.style.top = "0px";
      this.moveBox.current.style.left = "0px";
      this.moveBox.current.style.right = "initial";
      this.moveBox.current.style.bottom = "initial";
      this.moveBox.current.style.height = windowHeight + "px";
      this.moveBox.current.style.width = dockWidth + "px";
    } else if (direction === DOCK_RIGHT) {
      // Dock to right side
      this.moveBox.current.style.position = "fixed";
      this.moveBox.current.style.top = "0px";
      this.moveBox.current.style.right = "0px";
      this.moveBox.current.style.left = "initial";
      this.moveBox.current.style.bottom = "initial";
      this.moveBox.current.style.height = windowHeight + "px";
      this.moveBox.current.style.width = dockWidth + "px";
    } else if (direction === DIRECTION_BOTTOM) {
      // Dock to bottom (keeping for backward compatibility)
      this.moveBox.current.style.position = "fixed";
      this.moveBox.current.style.height = "300px";
      this.moveBox.current.style.top = "initial";
      this.moveBox.current.style.bottom = "0px";
      this.moveBox.current.style.width = "100%";
      this.moveBox.current.style.left = "0px";
      this.moveBox.current.style.right = "initial";
    }
  };

  render() {
    const {
      contentClassName,
      children,
      title,
      alwaysOn,
      noClose,
      actions,
      noResize,
      titleAction,
      absAppBar,
      noAppBar,
      appBarMenu,
    } = this.props;
    const { minified, externalWindow, dockDirection } = this.state;
    const getContent = () => {
      return (
        <div
          className="moveBox__window"
          onClick={this.moveToTop}
          ref={this.moveBox}
        >
          {!externalWindow && (
            <MoveBoxNavBar
              title={title}
              noClose={noClose}
              onClick={this.moveToTop}
              onTouchMove={this.onTouchMove}
              onTouchEnd={this.onTouchEnd}
              onTouchStart={this.onTouchStart}
              onMouseDown={this.onMouseDown}
              onMinify={this.onMinify}
              onClose={this.onClose}
              dockDirection={dockDirection}
              changeDockPosition={this.changeDockPosition}
              titleAction={titleAction}
            />
          )}
          {!noAppBar && (
            <MoveBoxAppBar absolute={absAppBar}>
              {appBarMenu && (appBarMenu.above || appBarMenu.below) && (
                <DropDownMenu>
                  {appBarMenu && appBarMenu.above ? appBarMenu.above : null}
                  {appBarMenu && appBarMenu.below ? appBarMenu.below : null}
                </DropDownMenu>
              )}
            </MoveBoxAppBar>
          )}
          <div
            className={
              contentClassName
                ? "moveBox__content " + contentClassName
                : "moveBox__content"
            }
            ref={this.moveBoxContent}
          >
            {children}
          </div>
          {actions ? <div className="moveBox__actions">{actions}</div> : null}
          {!externalWindow && !noResize ? (
            <div
              className={bemClassesFromModifiers("moveBox__resizer", [
                !actions && isOverflown(this.moveBoxContent.current)
                  ? "overflown"
                  : null,
              ])}
              onMouseDown={this.onResizeMouseDown}
            />
          ) : null}
        </div>
      );
    };
    return (
      <ExternalWindow
        title={title}
        onClose={this.onExternalWindowClose}
        onMinify={this.onMinify}
        externalWindow={externalWindow}
      >
        <MoveBoxWrapper {...this.props} {...this.state}>
          {getContent()}
          {minified || alwaysOn ? (
            <Fab
              onClick={this.onMinify}
              className="moveBox__expand"
              weight={this.props.fabWeight}
            >
              {this.props.expandIcon ? this.props.expandIcon : <ExpandIcon />}
            </Fab>
          ) : null}
        </MoveBoxWrapper>
      </ExternalWindow>
    );
  }
}

export default MoveBox;
